import { useState } from "react";
import {
  Calendar, Sparkles, Zap, Target, BookOpen, Brain, Star, ChevronRight,
  RefreshCw, CheckCircle2, XCircle, AlertCircle, Copy, ExternalLink,
} from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000";
const CARD = "bg-white/[0.04] border border-white/[0.07] rounded-2xl backdrop-blur-sm";
const BTN_PRIMARY = "inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white rounded-lg px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const BTN_SECONDARY = "inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.09] border border-white/[0.1] text-white/70 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50";

type Tab = "brief" | "calendar" | "hooks";
type Goal = "awareness" | "engagement" | "conversion";
type Pillar = "budgeting_tips" | "app_features" | "success_stories" | "financial_mindset" | "wave_system";

const PILLAR_LABELS: Record<Pillar, string> = {
  budgeting_tips: "Budgeting Tips",
  app_features: "App Features",
  success_stories: "Success Stories",
  financial_mindset: "Financial Mindset",
  wave_system: "Wave System",
};

const PILLAR_COLORS: Record<Pillar, string> = {
  budgeting_tips: "#007AFF",
  app_features: "#5856D6",
  success_stories: "#34C759",
  financial_mindset: "#FF9500",
  wave_system: "#32ADE6",
};

const GOAL_COLORS: Record<Goal, string> = {
  awareness: "#FF9500",
  engagement: "#007AFF",
  conversion: "#34C759",
};

const GOAL_LABELS: Record<Goal, string> = {
  awareness: "Reach",
  engagement: "Engage",
  conversion: "Convert",
};

const PLATFORMS = ["twitter", "linkedin", "instagram", "facebook", "tiktok", "reddit", "email"];
const PLATFORM_LABELS: Record<string, string> = {
  twitter: "Twitter/X", linkedin: "LinkedIn", instagram: "Instagram",
  facebook: "Facebook", tiktok: "TikTok", reddit: "Reddit",
  youtube_shorts: "YouTube", email: "Email", push: "Push",
};

const PILLARS: Pillar[] = ["budgeting_tips", "app_features", "success_stories", "financial_mindset", "wave_system"];

const HOOK_LIBRARY: Record<Pillar, Record<string, string[]>> = {
  budgeting_tips: {
    twitter: ["You're not broke. You're budgeting wrong. Here's the fix 🧵", "Stop budgeting monthly. Do THIS instead (biweekly method) ↓", "The #1 reason people can't stick to a budget (it's not willpower)"],
    linkedin: ["Most professionals lose $400/month to \"lifestyle creep\" without knowing it.", "I tracked every dollar for 90 days. Here's what shocked me.", "Why the 50/30/20 rule fails for biweekly earners — and what works instead."],
    instagram: ["POV: You actually have money left at the end of the month 💰", "Budget method that changed everything for me (swipe →)", "The reason your budget fails every month 👇"],
    tiktok: ["The budgeting trick your bank doesn't want you to know 🤫", "Wait until the end — this method saved me $600/month", "Budget fails be like... (then the fix) ✨"],
    reddit: ["Stopped using apps that budget monthly — here's why biweekly is better [breakdown]", "Anyone else find that pay-period budgeting beats monthly budgeting?"],
    email: ["The $0-based budget method that actually sticks (even on a tight income)", "Why your monthly budget is lying to you"],
    facebook: ["The simple budgeting shift that finally made money make sense for our family 💙", "If you struggle to save — this is why (and the fix)"],
  },
  app_features: {
    twitter: ["What if your budget automatically adjusted every time you got paid? 👀", "Bank sync + AI advisor + wave budgeting — all in one app. Here's how ↓"],
    linkedin: ["Automated budgeting is no longer a luxury — it's a necessity.", "How AI-powered budget tracking reduces financial stress for families."],
    instagram: ["Your budget app should do the math for you 📱✨", "When your bank account updates automatically 🤌 #WaveBudget"],
    tiktok: ["This app tells me where every dollar went before I even check #budgetapp", "Automatic budget update every payday? Yes please 💸"],
    reddit: ["Built a budget tracker that auto-adjusts for biweekly pay — here's what I learned", "Bank sync + wave-based budgeting — game changer for our household"],
    email: ["See every dollar in real time — how bank sync saves hours per week", "Your new AI budget advisor (inside WaveBudget)"],
    facebook: ["Finally found an app that works the way we actually get paid 💪", "AI budgeting advisor? Yes — and it's free to try."],
  },
  success_stories: {
    twitter: ["Paid off $14,000 in debt in 18 months. The tool I used ↓", "\"I finally have an emergency fund.\" — WaveBudget user story 🧵"],
    linkedin: ["A team member paid off student loans 2 years early using this one habit.", "Financial stress reduced. Productivity increased. Here's the connection."],
    instagram: ["Paid off debt ✅ Built savings ✅ Still lived my life ✅", "From $0 savings to $5k emergency fund in 8 months 🎉"],
    tiktok: ["How I went from overdraft every week to $3k saved this year 💸", "My friend paid off $8k using this budgeting method — here's how"],
    reddit: ["Success story: How wave budgeting helped me break the paycheck-to-paycheck cycle", "6 months update: From $0 savings to $4,200 emergency fund [method inside]"],
    email: ["\"I didn't think I could save anything.\" — Read Maria's story", "From overdraft fees every month to $5k saved: A real WaveBudget story"],
    facebook: ["From paycheck-to-paycheck to $3k savings — our family's story 🙏", "We paid off our car note early. Here's what we used."],
  },
  financial_mindset: {
    twitter: ["Rich people don't budget differently. They think differently. Thread 🧵", "The money belief keeping you broke (and how to replace it)"],
    linkedin: ["The most successful people I know treat their personal finances like a business.", "Financial clarity is a leadership skill. Here's how to develop it."],
    instagram: ["Your budget is not a punishment. It's a plan 🙌", "The money mindset shift that changed everything for me"],
    tiktok: ["The mindset shift about money nobody taught us in school 🎓", "Stop saying you're bad with money — this is why 👇"],
    reddit: ["The mindset shift that made budgeting actually stick for me [long post]", "How I stopped feeling guilty about spending and started feeling in control"],
    email: ["The one money belief that's costing you hundreds per month", "Why most budgets fail in week 2 (it's not the numbers)"],
    facebook: ["Budgeting is not about restriction. It's about peace of mind. 💙", "The money mindset shift our family needed — and how we made it."],
  },
  wave_system: {
    twitter: ["Monthly budgets don't work for biweekly earners. Here's why + the fix 🧵", "The \"wave\" budgeting method: budget with how you actually get paid ↓"],
    linkedin: ["Biweekly pay ≠ monthly budget. The math gap most employees ignore.", "The wave income model: why aligning your budget to your pay cycle changes everything."],
    instagram: ["Budget to your paycheck, not to the calendar 📅💡", "The wave method: your budget, your pay cycle, your rules 🌊"],
    tiktok: ["If you get paid every 2 weeks, stop using monthly budget apps 🌊", "The wave method explained in 60 seconds — this changed budgeting for me"],
    reddit: ["Why I switched from YNAB to a wave-based budgeting app [detailed comparison]", "Wave budgeting: budget by pay period, not month [how it works]"],
    email: ["If you're paid biweekly, you need to read this", "The wave system: your complete guide to pay-period budgeting"],
    facebook: ["If you get paid every 2 weeks — this budgeting approach was made for you 🌊", "Stop fighting your paycheck cycle. Work with it."],
  },
};

interface DayPlan {
  day: number;
  date: string;
  posts: {
    platform: string;
    pillar: string;
    pillarLabel: string;
    goal: string;
    goalLabel: string;
    hook: string;
    contentId?: string;
    articleTitle?: string;
    hasContent: boolean;
  }[];
}

interface PlanSummary {
  totalDays: number;
  totalPosts: number;
  postsWithContent: number;
  postsNeedingContent: number;
  platforms: string[];
  startDate: string;
  endDate: string;
}

function ContentStrategy() {
  const [tab, setTab] = useState<Tab>("brief");
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(["twitter", "linkedin", "instagram"]);
  const [postsPerDay, setPostsPerDay] = useState(2);
  const [generating, setGenerating] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [plan, setPlan] = useState<DayPlan[] | null>(null);
  const [summary, setSummary] = useState<PlanSummary | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [hookPilterFilter, setHookPillarFilter] = useState<Pillar>("budgeting_tips");
  const [hookPlatformFilter, setHookPlatformFilter] = useState("twitter");
  const [copied, setCopied] = useState<string | null>(null);

  const togglePlatform = (p: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
    );
  };

  const handleGenerate = async () => {
    if (selectedPlatforms.length === 0) { setError("Select at least one platform"); return; }
    setGenerating(true); setError(""); setSuccess(""); setPlan(null); setSummary(null);
    try {
      const token = localStorage.getItem("adminToken");
      const r = await fetch(`${API_URL}/api/admin/scheduler/generate-30-day-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ platforms: selectedPlatforms, postsPerDay }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Failed to generate plan");
      setPlan(data.plan);
      setSummary(data.summary);
      setSuccess(`Plan generated: ${data.summary.totalPosts} posts over 30 days (${data.summary.postsWithContent} with existing content)`);
    } catch (err: any) {
      setError(err.message || "Failed to generate plan");
    } finally {
      setGenerating(false);
    }
  };

  const handleScheduleAll = async () => {
    if (!plan) return;
    if (!confirm(`Schedule all ${summary?.totalPosts} posts? This will create ScheduledPost records for posts with existing content.`)) return;
    setScheduling(true); setError(""); setSuccess("");
    try {
      const token = localStorage.getItem("adminToken");
      const r = await fetch(`${API_URL}/api/admin/scheduler/confirm-30-day-plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Failed to schedule plan");
      setSuccess(data.message);
    } catch (err: any) {
      setError(err.message || "Failed to schedule plan");
    } finally {
      setScheduling(false);
    }
  };

  const copyHook = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(text);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Content Strategy</h1>
          <p className="text-white/40 text-sm mt-0.5">WaveBudget 30-day social media growth engine</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white/[0.04] border border-white/[0.07] rounded-xl p-1 w-fit">
        {([
          { id: "brief", label: "Strategy Brief", icon: <BookOpen size={14} /> },
          { id: "calendar", label: "30-Day Calendar", icon: <Calendar size={14} /> },
          { id: "hooks", label: "Hook Library", icon: <Sparkles size={14} /> },
        ] as { id: Tab; label: string; icon: React.ReactNode }[]).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.id ? 'bg-primary text-white' : 'text-white/50 hover:text-white/80'}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {error && <div className="bg-red/10 border border-red/20 text-red rounded-lg px-4 py-3 text-sm">{error}</div>}
      {success && <div className="bg-green/10 border border-green/20 text-green rounded-lg px-4 py-3 text-sm">{success}</div>}

      {/* ── Tab: Strategy Brief ── */}
      {tab === "brief" && (
        <div className="space-y-5">
          {/* Niche overview */}
          <div className={`${CARD} p-6`}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                <Target size={16} className="text-primary" />
              </div>
              <h2 className="text-white font-semibold">WaveBudget Niche Intelligence</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              {[
                { label: "Primary Niche", value: "Personal finance / biweekly budget tracking" },
                { label: "Audience", value: "Paycheck-to-paycheck earners, families, gig workers" },
                { label: "Pain Points", value: "Overspending, no savings, debt, irregular income" },
                { label: "Differentiator", value: "Wave-based income model — budget by pay period, not month" },
                { label: "Platforms", value: "Twitter, LinkedIn, Instagram, TikTok, Reddit, Facebook" },
                { label: "CTA", value: "Try WaveBudget free → iOS & Android" },
              ].map((item, i) => (
                <div key={i} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3">
                  <p className="text-white/40 text-xs mb-1">{item.label}</p>
                  <p className="text-white/90 text-sm font-medium">{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Content Pillars */}
          <div className={`${CARD} p-6`}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-orange/20 flex items-center justify-center">
                <Brain size={16} className="text-orange" />
              </div>
              <h2 className="text-white font-semibold">5 Content Pillars</h2>
            </div>
            <div className="space-y-3">
              {PILLARS.map(pillar => (
                <div key={pillar} className="flex items-start gap-3 p-3 bg-white/[0.02] border border-white/[0.05] rounded-xl">
                  <div className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: PILLAR_COLORS[pillar] }} />
                  <div>
                    <p className="text-white font-semibold text-sm">{PILLAR_LABELS[pillar]}</p>
                    <p className="text-white/40 text-xs mt-0.5">
                      {pillar === 'budgeting_tips' && "Practical budgeting advice: zero-based budgeting, saving tips, debt payoff strategies for biweekly earners."}
                      {pillar === 'app_features' && "Show how WaveBudget features (wave model, AI advisor, bank sync) solve real money problems."}
                      {pillar === 'success_stories' && "Real-user outcomes: paid off debt, built emergency fund, broke the paycheck-to-paycheck cycle."}
                      {pillar === 'financial_mindset' && "Mindset shifts: money isn't bad, budgeting is freedom, small habits compound."}
                      {pillar === 'wave_system' && "Explain the wave-based income model: why budgeting per pay period beats monthly for biweekly earners."}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Content Goals */}
          <div className={`${CARD} p-6`}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-green/20 flex items-center justify-center">
                <Zap size={16} className="text-green" />
              </div>
              <h2 className="text-white font-semibold">30-Day Goal Distribution</h2>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {(Object.entries(GOAL_LABELS) as [Goal, string][]).map(([goal, label]) => (
                <div key={goal} className="text-center p-4 bg-white/[0.02] border border-white/[0.05] rounded-xl">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2" style={{ background: `${GOAL_COLORS[goal]}20` }}>
                    <div className="w-3 h-3 rounded-full" style={{ background: GOAL_COLORS[goal] }} />
                  </div>
                  <p className="text-white font-semibold text-sm">{label}</p>
                  <p className="text-white/40 text-xs mt-0.5">
                    {goal === 'awareness' ? '~40% of posts — grow reach, new audiences' : goal === 'engagement' ? '~35% of posts — spark conversation' : '~25% of posts — drive app downloads'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: 30-Day Calendar ── */}
      {tab === "calendar" && (
        <div className="space-y-5">
          {/* Controls */}
          <div className={`${CARD} p-5`}>
            <h2 className="text-white font-semibold mb-4">Plan Configuration</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-white/50 mb-2">Platforms</label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.map(p => (
                    <button key={p} onClick={() => togglePlatform(p)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${selectedPlatforms.includes(p) ? 'bg-primary/20 border-primary/40 text-primary' : 'bg-white/[0.04] border-white/[0.08] text-white/40 hover:text-white/70'}`}>
                      {PLATFORM_LABELS[p]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div>
                  <label className="block text-xs font-medium text-white/50 mb-1.5">Posts per day</label>
                  <select value={postsPerDay} onChange={e => setPostsPerDay(parseInt(e.target.value))}
                    className="bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-primary/50 transition-all">
                    {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={handleGenerate} disabled={generating || selectedPlatforms.length === 0} className={BTN_PRIMARY}>
                  {generating ? <><RefreshCw size={14} className="animate-spin" /> Generating…</> : <><Sparkles size={14} /> Generate 30-Day Plan</>}
                </button>
                {plan && (
                  <button onClick={handleScheduleAll} disabled={scheduling} className={BTN_SECONDARY}>
                    {scheduling ? <><RefreshCw size={14} className="animate-spin" /> Scheduling…</> : <><Calendar size={14} /> Schedule All</>}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Summary */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: "Total Posts", value: summary.totalPosts, cls: "text-white" },
                { label: "With Content", value: summary.postsWithContent, cls: "text-green" },
                { label: "Need Content", value: summary.postsNeedingContent, cls: summary.postsNeedingContent > 0 ? "text-orange" : "text-white/40" },
                { label: "Platforms", value: summary.platforms.length, cls: "text-primary" },
              ].map((s, i) => (
                <div key={i} className={`${CARD} p-4`}>
                  <p className="text-white/40 text-xs mb-1">{s.label}</p>
                  <p className={`text-2xl font-bold ${s.cls}`}>{s.value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Calendar grid */}
          {plan && (
            <div className={`${CARD} p-5`}>
              <h2 className="text-white font-semibold mb-4">30-Day Calendar Preview</h2>
              {(summary?.postsNeedingContent ?? 0) > 0 && (
                <div className="bg-orange/10 border border-orange/20 text-orange rounded-lg px-4 py-3 text-sm mb-4 flex items-center gap-2">
                  <AlertCircle size={14} />
                  {summary?.postsNeedingContent} posts need content — generate content in Articles Manager first, then regenerate the plan.
                </div>
              )}
              <div className="grid grid-cols-7 gap-1.5">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                  <div key={d} className="py-1.5 text-center text-xs font-semibold text-white/25 uppercase tracking-wider">{d}</div>
                ))}
                {/* Empty cells for first week offset */}
                {Array.from({ length: new Date(plan[0]?.date + 'T12:00:00').getDay() }, (_, i) => (
                  <div key={`empty-${i}`} className="h-20 rounded-lg bg-white/[0.01]" />
                ))}
                {plan.map((day) => {
                  const pillar = day.posts[0]?.pillar as Pillar;
                  const goal = day.posts[0]?.goal as Goal;
                  const allHaveContent = day.posts.every(p => p.hasContent);
                  const noneHaveContent = day.posts.every(p => !p.hasContent);
                  return (
                    <div key={day.day} className="h-20 rounded-lg border border-white/[0.06] bg-white/[0.02] p-1.5 flex flex-col justify-between"
                      style={{ borderLeftColor: PILLAR_COLORS[pillar] || '#666', borderLeftWidth: 2 }}>
                      <div className="flex items-center justify-between">
                        <span className="text-white/60 text-xs font-medium">{day.day}</span>
                        {allHaveContent ? <CheckCircle2 size={9} className="text-green" /> : noneHaveContent ? <XCircle size={9} className="text-red/60" /> : <AlertCircle size={9} className="text-orange" />}
                      </div>
                      <div>
                        <p className="text-white/70 text-[9px] font-semibold leading-tight truncate">
                          {PILLAR_LABELS[pillar] || pillar}
                        </p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-[8px] font-semibold px-1 rounded" style={{ background: `${GOAL_COLORS[goal] || '#666'}20`, color: GOAL_COLORS[goal] || '#999' }}>
                            {GOAL_LABELS[goal] || goal}
                          </span>
                          <span className="text-white/25 text-[8px]">{day.posts.length}p</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-white/[0.06]">
                <span className="text-white/40 text-xs">Legend:</span>
                {PILLARS.map(p => (
                  <div key={p} className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full" style={{ background: PILLAR_COLORS[p] }} />
                    <span className="text-white/50 text-xs">{PILLAR_LABELS[p]}</span>
                  </div>
                ))}
                <div className="flex items-center gap-1 ml-4">
                  <CheckCircle2 size={10} className="text-green" />
                  <span className="text-white/50 text-xs">Has content</span>
                </div>
                <div className="flex items-center gap-1">
                  <AlertCircle size={10} className="text-orange" />
                  <span className="text-white/50 text-xs">Partial</span>
                </div>
                <div className="flex items-center gap-1">
                  <XCircle size={10} className="text-red/60" />
                  <span className="text-white/50 text-xs">No content</span>
                </div>
              </div>
            </div>
          )}

          {!plan && !generating && (
            <div className={`${CARD} flex flex-col items-center justify-center py-20 text-center gap-3`}>
              <Calendar size={36} className="text-white/15" />
              <p className="text-white/40 text-sm">Configure platforms and click "Generate 30-Day Plan" to preview your content calendar.</p>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Hook Library ── */}
      {tab === "hooks" && (
        <div className="space-y-5">
          <div className={`${CARD} p-5`}>
            <h2 className="text-white font-semibold mb-4">Hook Library — WaveBudget</h2>
            <div className="flex flex-wrap gap-3 mb-5">
              <div>
                <label className="block text-xs font-medium text-white/40 mb-1.5">Pillar</label>
                <select value={hookPilterFilter} onChange={e => setHookPillarFilter(e.target.value as Pillar)}
                  className="bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-primary/50 transition-all">
                  {PILLARS.map(p => <option key={p} value={p}>{PILLAR_LABELS[p]}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-white/40 mb-1.5">Platform</label>
                <select value={hookPlatformFilter} onChange={e => setHookPlatformFilter(e.target.value)}
                  className="bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-primary/50 transition-all">
                  {PLATFORMS.map(p => <option key={p} value={p}>{PLATFORM_LABELS[p]}</option>)}
                </select>
              </div>
            </div>

            {/* Pillar tag */}
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: PILLAR_COLORS[hookPilterFilter] }} />
              <span className="text-white/60 text-xs font-semibold">{PILLAR_LABELS[hookPilterFilter]}</span>
              <ChevronRight size={12} className="text-white/20" />
              <span className="text-white/40 text-xs">{PLATFORM_LABELS[hookPlatformFilter]}</span>
            </div>

            <div className="space-y-2">
              {(HOOK_LIBRARY[hookPilterFilter]?.[hookPlatformFilter] ?? HOOK_LIBRARY[hookPilterFilter]?.email ?? []).map((hook, i) => (
                <div key={i} className="flex items-start gap-3 p-3.5 bg-white/[0.03] border border-white/[0.06] rounded-xl hover:bg-white/[0.05] transition-colors group">
                  <Star size={13} className="text-orange mt-0.5 flex-shrink-0" />
                  <p className="text-white/80 text-sm flex-1 leading-relaxed">{hook}</p>
                  <button onClick={() => copyHook(hook)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-white/40 hover:text-white p-1">
                    {copied === hook ? <CheckCircle2 size={14} className="text-green" /> : <Copy size={14} />}
                  </button>
                </div>
              ))}
              {!(HOOK_LIBRARY[hookPilterFilter]?.[hookPlatformFilter]?.length) && (
                <div className="text-white/30 text-sm text-center py-8">No hooks for this combination — showing email hooks instead.</div>
              )}
            </div>
          </div>

          {/* All pillars overview */}
          <div className={`${CARD} p-5`}>
            <h3 className="text-white font-semibold mb-4">All Pillar Hooks — Quick View</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {PILLARS.map(pillar => (
                <div key={pillar} className="p-3.5 bg-white/[0.02] border border-white/[0.05] rounded-xl"
                  style={{ borderLeftColor: PILLAR_COLORS[pillar], borderLeftWidth: 2 }}>
                  <p className="text-white font-semibold text-xs mb-2" style={{ color: PILLAR_COLORS[pillar] }}>
                    {PILLAR_LABELS[pillar]}
                  </p>
                  {(HOOK_LIBRARY[pillar]?.twitter ?? HOOK_LIBRARY[pillar]?.email ?? []).slice(0, 2).map((h, i) => (
                    <div key={i} className="flex items-start gap-1.5 mb-1.5">
                      <div className="w-1 h-1 rounded-full bg-white/25 mt-1.5 flex-shrink-0" />
                      <p className="text-white/50 text-xs leading-relaxed line-clamp-2">{h}</p>
                    </div>
                  ))}
                  <button onClick={() => { setTab("hooks"); setHookPillarFilter(pillar); }}
                    className="mt-2 text-primary text-xs font-medium hover:text-primary/80 transition-colors inline-flex items-center gap-1">
                    See all <ExternalLink size={9} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContentStrategy;
